import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, Eye, EyeOff, Facebook, Chrome } from 'lucide-react';
import { authAPI } from './services/api';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './components/LanguageSwitcher';
import UserMenu from './components/UserMenu';

const AuthPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    agreeTerms: false
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // 清除該欄位的錯誤
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = '請輸入電子郵件';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = '電子郵件格式不正確';
    }

    if (!formData.password) {
      newErrors.password = '請輸入密碼';
    } else if (formData.password.length < 6) {
      newErrors.password = '密碼至少需要 6 個字符';
    }

    if (!isLogin) {
      if (!formData.name) {
        newErrors.name = '請輸入姓名';
      }

      if (!formData.phone) {
        newErrors.phone = '請輸入手機號碼';
      } else if (!/^[0-9]{10,11}$/.test(formData.phone.replace(/[-\s]/g, ''))) {
        newErrors.phone = '手機號碼格式不正確';
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = '請確認密碼';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = '兩次密碼輸入不一致';
      }

      if (!formData.agreeTerms) {
        newErrors.agreeTerms = '請同意服務條款';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      if (isLogin) {
        // 登入
        const response = await authAPI.login({
          email: formData.email,
          password: formData.password,
        });
        
        console.log('登入響應:', response); // 調試用
        
        // 確保從正確的位置獲取用戶名稱
        const userName = response.name || response.data?.name || '用戶';
        
        alert(`登入成功！歡迎回來，${userName}！`);
        navigate('/');
      } else {
        // 註冊
        const response = await authAPI.register({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
        });
        
        console.log('註冊響應:', response); // 調試用
        
        // 確保從正確的位置獲取用戶名稱
        const userName = response.name || response.data?.name || formData.name;
        
        alert(`註冊成功！歡迎，${userName}！`);
        navigate('/');
      }
    } catch (error) {
      console.error('認證錯誤:', error);
      console.error('錯誤詳情:', error.response); // 調試用
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          '操作失敗，請稍後再試';
      setErrors({ submit: errorMessage });
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    alert(`使用 ${provider} 登入`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl grid md:grid-cols-2 gap-8 items-center">
        {/* 左側品牌區 */}
        <div className="hidden md:block">
          <div className="text-center">
            <div className="inline-block">
              <div className="w-32 h-32 bg-gradient-to-br from-orange-500 to-pink-500 rounded-3xl flex items-center justify-center text-white mb-6 transform hover:scale-105 transition shadow-2xl">
                <span className="text-6xl font-bold">PG</span>
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              歡迎來到 PG Mall
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              馬來西亞第一購物平台
            </p>
            <div className="space-y-4 text-left max-w-md mx-auto">
              <div className="flex items-center gap-3 text-gray-700">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  ✓
                </div>
                <span>數百萬件商品可選</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  ✓
                </div>
                <span>安全支付保護</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  ✓
                </div>
                <span>快速配送服務</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  ✓
                </div>
                <span>7天無理由退換</span>
              </div>
            </div>
          </div>
        </div>

        {/* 右側表單區 */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Tab 切換 */}
          <div className="flex mb-8 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 rounded-lg font-semibold transition ${
                isLogin
                  ? 'bg-white text-orange-500 shadow'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              登入
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 rounded-lg font-semibold transition ${
                !isLogin
                  ? 'bg-white text-orange-500 shadow'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              註冊
            </button>
          </div>

          {/* 表單 */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 註冊專用：姓名 */}
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  姓名 *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="請輸入您的姓名"
                    className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.name
                        ? 'border-red-500 focus:ring-red-200'
                        : 'border-gray-300 focus:ring-orange-200'
                    }`}
                  />
                </div>
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>
            )}

            {/* 電子郵件 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                電子郵件 *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="example@email.com"
                  className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.email
                      ? 'border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:ring-orange-200'
                  }`}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* 註冊專用：手機號碼 */}
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  手機號碼 *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="012-3456789"
                    className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.phone
                        ? 'border-red-500 focus:ring-red-200'
                        : 'border-gray-300 focus:ring-orange-200'
                    }`}
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}
              </div>
            )}

            {/* 密碼 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                密碼 *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="至少 6 個字符"
                  className={`w-full pl-11 pr-11 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.password
                      ? 'border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:ring-orange-200'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>

            {/* 註冊專用：確認密碼 */}
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  確認密碼 *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="請再次輸入密碼"
                    className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.confirmPassword
                        ? 'border-red-500 focus:ring-red-200'
                        : 'border-gray-300 focus:ring-orange-200'
                    }`}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                )}
              </div>
            )}

            {/* 登入專用：記住我 & 忘記密碼 */}
            {isLogin && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-orange-500 rounded"
                  />
                  <span className="text-sm text-gray-600">記住我</span>
                </label>
                <a href="#" className="text-sm text-orange-500 hover:underline">
                  忘記密碼？
                </a>
              </div>
            )}

            {/* 註冊專用：同意條款 */}
            {!isLogin && (
              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-orange-500 rounded mt-1"
                  />
                  <span className="text-sm text-gray-600">
                    我已閱讀並同意{' '}
                    <a href="#" className="text-orange-500 hover:underline">
                      服務條款
                    </a>{' '}
                    和{' '}
                    <a href="#" className="text-orange-500 hover:underline">
                      隱私政策
                    </a>
                  </span>
                </label>
                {errors.agreeTerms && (
                  <p className="text-red-500 text-sm mt-1">{errors.agreeTerms}</p>
                )}
              </div>
            )}

            {/* 提交按鈕 */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-pink-500 text-white py-3 rounded-lg font-bold hover:from-orange-600 hover:to-pink-600 transition transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? '處理中...' : (isLogin ? '登入' : '註冊')}
            </button>

            {/* 顯示提交錯誤 */}
            {errors.submit && (
              <p className="text-red-500 text-sm text-center">{errors.submit}</p>
            )}
          </form>

          {/* 分隔線 */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">或使用以下方式{isLogin ? '登入' : '註冊'}</span>
            </div>
          </div>

          {/* 社交登入 */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleSocialLogin('Google')}
              className="flex items-center justify-center gap-2 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              <Chrome size={20} className="text-red-500" />
              <span className="font-medium text-gray-700">Google</span>
            </button>
            <button
              onClick={() => handleSocialLogin('Facebook')}
              className="flex items-center justify-center gap-2 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              <Facebook size={20} className="text-blue-600" />
              <span className="font-medium text-gray-700">Facebook</span>
            </button>
          </div>

          {/* 底部提示 */}
          <div className="mt-6 text-center">
            {isLogin ? (
              <p className="text-sm text-gray-600">
                還沒有帳號？{' '}
                <button
                  onClick={() => setIsLogin(false)}
                  className="text-orange-500 font-semibold hover:underline"
                >
                  立即註冊
                </button>
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                已經有帳號了？{' '}
                <button
                  onClick={() => setIsLogin(true)}
                  className="text-orange-500 font-semibold hover:underline"
                >
                  立即登入
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;